import { CSSInterpolation } from '@emotion/css/create-instance'
import Lottie from 'lottie-react-web'
import React, { type ReactNode } from 'react'

import { type ColorToken, CssVars } from '../../themes'
import { getColorValue, useTheme } from '../ThemeProvider'
import spinner12 from './spinner12'
import spinner16 from './spinner16'
import spinner24 from './spinner24'
import spinnerHero from './spinnerHero'
import spinnerOverlay from './spinnerOverlay'

/**
 * REMOVING THIS LINE WILL BREAK VITE APPS
 * There is a bug in rollup/vite that makes consuming
 * CJS packages that are imported via an ESM package
 * completely break. The bug has to do with how
 * the internal module loader determines which object
 * to load. This specific error only seems to impact
 * the spinner components since they are the only
 * ones that use lottie-react-web.
 *
 * This fix comes from this thread:
 * https://github.com/vitejs/vite/issues/2139#issuecomment-824557740
 *
 * Additional reading:
 * - https://github.com/rollup/plugins/issues/481
 * - https://github.com/rollup/plugins/issues/654
 */
// @ts-expect-error error TS2339: Property 'default' does not exist on type 'typeof Lottie' and that's expected
const ESM_INTEROP_Lottie = Lottie.default ? Lottie.default : Lottie

type BaseSpinnerProps = {
  /**
   * By default the spinner will use the local text color. Use this prop if you wish to
   * override that behavior.
   */
  color?: ColorToken | null

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this
   * sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

function SpinnerWrapper({
  children,
  color,
  overrideRootStyles,
}: BaseSpinnerProps & {
  children: ReactNode
}) {
  const theme = useTheme()

  return (
    <div
      aria-busy="true"
      css={[
        {
          color: color ? getColorValue(color, theme) : CssVars.textColor,

          'svg path': {
            stroke: 'currentColor',
          },
        },
        overrideRootStyles,
        {
          // HACK(casper): This is to keep the styling for the comment below while not appling styling directly to Lottie,
          // so that we avoid noisey Lottie warnings in console. (https://github.com/felippenardi/lottie-react-web/issues/24)
          // Note(@auster.chen): This is to ensure that positioning of svg inside div is right and nothing is cut off.
          '> div': {
            display: 'flex',
          },
        },
      ]}
      data-loading="true"
      role="alert"
    >
      {children}
    </div>
  )
}

/**
 * These components render various sizes of spinners, to use in cases like when UI is not
 * yet ready to be rendered due to pending data fetches.
 *
 * See also `Skeleton`, which can provide a better UX if you know the specific shape of UI
 * that will be rendered once the data is loaded.
 */
export function Spinner12(props: BaseSpinnerProps) {
  return (
    <SpinnerWrapper {...props}>
      <ESM_INTEROP_Lottie
        ariaRole={null}
        height={12}
        options={{
          animationData: spinner12,
        }}
        width={12}
      />
    </SpinnerWrapper>
  )
}

export function Spinner16(props: BaseSpinnerProps) {
  return (
    <SpinnerWrapper {...props}>
      <ESM_INTEROP_Lottie
        ariaRole={null}
        height={16}
        options={{
          animationData: spinner16,
        }}
        width={16}
      />
    </SpinnerWrapper>
  )
}

export function Spinner24(props: BaseSpinnerProps) {
  return (
    <SpinnerWrapper {...props}>
      <ESM_INTEROP_Lottie
        ariaRole={null}
        height={24}
        options={{
          animationData: spinner24,
        }}
        width={24}
      />
    </SpinnerWrapper>
  )
}

export function SpinnerHero(props: BaseSpinnerProps) {
  return (
    <SpinnerWrapper {...props}>
      <ESM_INTEROP_Lottie
        ariaRole={null}
        height={64}
        options={{
          animationData: spinnerHero,
        }}
        width={64}
      />
    </SpinnerWrapper>
  )
}

export function SpinnerOverlay(props: BaseSpinnerProps) {
  return (
    <SpinnerWrapper {...props}>
      <ESM_INTEROP_Lottie
        ariaRole={null}
        height={80}
        options={{
          animationData: spinnerOverlay,
        }}
        width={80}
      />
    </SpinnerWrapper>
  )
}
